/**
 * Which captured frontend errors are worth reporting, and which are the visitor's environment talking.
 *
 * Every pattern that gets through here is a GitHub issue on first production occurrence. What the list
 * covers, why it is explicit rather than a catch-all by type, and what each shape rule proves:
 * docs/notes/app/frontend-error-detection.md
 */
/**
 * Substrings that are never our code. Matched case-insensitively against `${name} ${message}`.
 *
 * Chunk-load / version-skew wordings are NOT here — `isChunkLoadError` in `route-error-classify.ts`
 * owns those, walks the `cause` chain for them, and is asked first by `logFrontendError`.
 */
const NON_ACTIONABLE_ERROR_PATTERNS = [
  // A cross-origin script threw and the browser withheld the details. Nothing to act on by construction.
  'script error.',
  // Microsoft Outlook/Office SafeLinks scans links in an embedded WebView whose injected script fails
  // with `Object Not Found Matching Id:<N>, MethodName:update, ParamCount:<N>`.
  'object not found matching id',
  // An injected script (bot instrumentation / an extension) monkey-patches `appendChild` and JSON.stringifies
  // the node React just mounted, which always throws: React's `__reactFiber$…` property points back at the
  // element, so the cycle is guaranteed. Matched on the fiber key rather than on `converting circular structure
  // to json`, so a genuine circular-JSON bug in our own serialization still reports — we never stringify DOM.
  '__reactfiber',
  // Crypto-wallet extensions inject a provider script that pokes at `window.ethereum` on a page with no
  // wallet integration at all. We never touch it, so an error naming it is by definition not ours.
  'window.ethereum',
  // Firefox-iOS and Brave-iOS inject a `__firefox__` reader/scroll helper into every page; it fails on
  // pages it cannot parse, as either `Can't find variable: __firefox__` or `undefined is not an object
  // (evaluating 'window.__firefox__.reader')`.
  '__firefox__',
  // A content script calling the WebExtension messaging API, refused because the tab it named is gone.
  // `runtime.sendMessage` exists only in an extension context and appears nowhere in our source. It
  // arrives with no stack, so the frame-based gate below cannot see it.
  'runtime.sendmessage',
  // Extensions and page translators mutate the DOM out from under React, making its next
  // insertBefore/removeChild fail. Not reproducible without the extension.
  "failed to execute 'insertbefore' on 'node'",
  "failed to execute 'removechild' on 'node'",
  'the node before which the new node is to be inserted is not a child of this node',
  'the object can not be found here',
  "cannot read properties of null (reading 'parentnode')",
  // Transient request/stream aborts: the visitor navigated away or the connection dropped mid-stream.
  'aborterror',
  'the operation was aborted',
  // The View Transitions API rejects `.ready` with `InvalidStateError: Transition was aborted because of
  // invalid state` when a transition is superseded (re-navigation, tab hidden). Benign, and this app
  // runs view transitions on every flow step — see `flowViewTransition`.
  'transition was aborted',
  'the connection was closed',
  'stream lifetime exceeded',
  // A benign layout-observer feedback loop the browser reports as an error.
  'resizeobserver loop',
  // React's own `getDataFromCustomEvent` has a `typeof null === 'object'` hole: `'data' in null` is a TypeError,
  // and a real browser never produces a composition event with a null `detail` — the reports come from injected
  // scripts dispatching synthetic ones. Matched on the property name too, so our own `in`-on-null bug would name
  // a different property and still report.
  "cannot use 'in' operator to search for 'data' in",
]

// What makes a stack frame OURS: a build serves every shipped line from `/assets/`, the dev server from
// `/src/`. Without the second, `bun dev` has no `/assets/` frame at all and the rule below drops everything.
const APP_CODE_FRAME_MARKERS = ['/assets/', '/src/']

/** V8: `    at fnName (url:line:col)`. */
const V8_FRAME = /^at\s/
/**
 * WebKit: `fnName@url:line:col`, `fnName@[native code]`, or `fnName@` / `@` when the source has no URL.
 * The part after `@` is matched strictly — a full `scheme://…`, `[native code]`, or nothing — precisely
 * so a MESSAGE containing an `@` (`Invalid email foo@bar.com`) is not mistaken for a frame. The name may
 * contain spaces, because WebKit really does emit some (`global code@…`, `eval code@…`).
 */
const WEBKIT_FRAME = /^.*@(\[native code\]|[a-z][a-z0-9+.-]*:\/\/\S*|)$/i

/** The lines of a stack that are real frames, skipping the message header V8 puts on the first line. */
function stackFrames(stack: string): string[] {
  return stack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '' && (V8_FRAME.test(line) || WEBKIT_FRAME.test(line)))
}

/**
 * Whether a stack proves the error came from code we do not ship: it HAS frames, and not one of them is
 * in our own bundle. Why that is safe against censoring our own crashes: docs/notes/app/frontend-error-detection.md
 */
export function isUnattributableThirdPartyError(stack: string | undefined): boolean {
  if (!stack) return false
  const frames = stackFrames(stack)
  if (frames.length === 0) return false
  return !frames.some((frame) => APP_CODE_FRAME_MARKERS.some((marker) => frame.includes(marker)))
}

/**
 * A `RangeError: Maximum call stack size exceeded` is only ever produced by runaway recursion, and its
 * stack is the tell-tale: a tight cycle of a handful of frames repeated until the engine's limit. That
 * shape is what distinguishes a genuine overflow from an unrelated error whose message merely contains
 * the phrase, which is what keeps the rule below narrow.
 *
 * Parses both WebKit (`name@url:line:col`) and V8 (`    at name (url:line:col)`) frames.
 */
export function isStackOverflowRecursionCycle(stack: string | undefined): boolean {
  if (!stack) return false
  const named: string[] = []
  for (const raw of stack.split('\n')) {
    const line = raw.trim()
    if (!line) continue
    let name: string | undefined
    if (line.startsWith('at ')) {
      name = line.slice(3).split(' ')[0]
      if (!name || name.startsWith('(')) name = undefined // anonymous / "at (native)"
    } else {
      const at = line.indexOf('@')
      if (at > 0) name = line.slice(0, at)
    }
    if (name) named.push(name)
  }
  // A tight recursion cycle: many frames, only a couple of distinct functions.
  return named.length >= 20 && new Set(named).size <= 3
}

/**
 * A rejection whose reason is a raw DOM Event rather than an Error, reported stackless — nothing triage
 * can act on, and nothing we ship rejects with one. Complements {@link isUnattributableThirdPartyError},
 * which needs frames to exist first. Why the shape and not one exact string: docs/notes/app/frontend-error-detection.md
 */
export function isStacklessEventRejection(name: string, message: string, stack: string | undefined): boolean {
  if (stack || name !== 'ObjectError') return false
  // The message is produced by `safeStringify` (plain JSON.stringify) on a non-Error object.
  let parsed: unknown
  try {
    parsed = JSON.parse(message)
  } catch {
    return false
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false
  const keys = Object.keys(parsed)
  // Event-shaped: empty (prototype-held `isTrusted`), or `isTrusted` plus at most a couple of stray own
  // properties. A rejection carrying real content — several fields — stays reportable.
  if (keys.length === 0) return true
  return 'isTrusted' in parsed && typeof parsed.isTrusted === 'boolean' && keys.length <= 3
}

/**
 * The one gate every report path asks, so none of them can drift onto a weaker one.
 *
 * Ordered cheapest-decisive first, but each rule is independent — the order is not load-bearing.
 */
export function shouldReportFrontendError(name: string, message: string, stack?: string): boolean {
  // Runaway recursion overflowing the (much smaller) WebKit/iOS call stack, thrown deep inside a vendor's
  // recursive serialization and surfaced to the global error handler. No application frame is involved.
  // Gated on the recursion-cycle stack shape so only genuine overflows are dropped, not an unrelated
  // error that merely mentions the phrase.
  if (
    name.toLowerCase().includes('rangeerror') &&
    message.toLowerCase().includes('maximum call stack size exceeded') &&
    isStackOverflowRecursionCycle(stack)
  ) {
    return false
  }
  // Minified junk from an injected script: no stable message to match, and no frame we could map back.
  if (isUnattributableThirdPartyError(stack)) return false
  // A rejected DOM Event with no stack — a report whose entire content is `{"isTrusted":true}`.
  if (isStacklessEventRejection(name, message, stack)) return false

  const haystack = `${name} ${message}`.toLowerCase()
  return !NON_ACTIONABLE_ERROR_PATTERNS.some((pattern) => haystack.includes(pattern))
}
